/**
 * mockData.ts — sample data for the v2 preview route at /preview/:slug.
 *
 * Hardcoded so layout iteration is independent of D1/Worker state. When the
 * v2 layout locks, this file gets deleted and PreviewPortal swaps to the
 * live portalApi calls.
 *
 * Photo URLs use picsum.photos with deterministic seeds so the same "photo"
 * loads consistently across reloads.
 */

export interface MockTask {
  id: string;
  task_text: string;
  task_description?: string;
  task_group?: string;
  is_complete: boolean;
  is_critical?: boolean;
  completed_date?: string;
  due_date?: string;
}

export interface MockPermit {
  id: string;
  permit_type: string;
  jurisdiction: string;
  permit_number?: string;
  status: string;
  application_date?: string;
  issued_date?: string;
  published_summary?: string;
}

export interface MockPhoto {
  id: string;
  url: string;       // full-size
  thumb: string;     // thumbnail
  caption?: string;
  source: 'pm' | 'client';
  uploaded_at: string;
}

export interface MockUpdate {
  id: string;
  date: string;        // ISO
  log_type: string;
  body: string;        // published_summary
  phase_id?: string;
  /** PM toggled "Pin to Headlines" — shows in top section + flagged 📌 inside phase. */
  pinned?: boolean;
}

export interface MockWeeklyPhotoSet {
  id: string;
  week_label: string;       // 'Week of May 5, 2026'
  description?: string;
  photos: MockPhoto[];
}

export interface MockPhotoBlock {
  id: string;
  category: 'pre-construction' | 'construction';
  weeks: MockWeeklyPhotoSet[];
}

export interface MockPhase {
  id: string;
  phase_number: number;
  phase_name: string;
  status: 'Not Started' | 'In Progress' | 'Complete' | 'On Hold';
  percent_complete: number;
  target_start_date?: string;
  target_end_date?: string;
  actual_start_date?: string;
  /** Optional rolling client-facing narrative (phases.published_notes). */
  narrative?: string;
  tasks: MockTask[];
  permits: MockPermit[];
  updates: MockUpdate[];
}

export interface MockProject {
  id: string;
  slug: string;
  name: string;
  client_name: string;
  city_state: string;
  overall_status: string;
  target_completion_date: string;
  color_tag: string;
  last_published_at: string;
  phases: MockPhase[];
  photoBlocks: MockPhotoBlock[];
  clientUploads: MockPhoto[];
  /** Convenience accessor for total photos across all blocks + client uploads. */
  totalPhotoCount: number;
}

// Helper for picsum.photos URLs — deterministic by seed
const photo = (seed: string, w = 1200, h = 800) =>
  `https://picsum.photos/seed/${seed}/${w}/${h}`;
const thumb = (seed: string) =>
  `https://picsum.photos/seed/${seed}/400/400`;

// Photo helpers — generate weekly photo bundles for the dedicated Photos page
const makePMWeeklyPhotos = (
  weekKey: string,
  count: number,
  captions: string[] = [],
): MockPhoto[] =>
  Array.from({ length: count }, (_, i) => ({
    id: `${weekKey}-${i}`,
    url: photo(`${weekKey}-${i}`),
    thumb: thumb(`${weekKey}-${i}`),
    caption: captions[i],
    source: 'pm' as const,
    uploaded_at: `2026-${weekKey.slice(0, 4)}T14:00:00Z`,
  }));

const makeClientUploads = (count: number): MockPhoto[] =>
  Array.from({ length: count }, (_, i) => ({
    id: `client-${i}`,
    url: photo(`client-up-${i}`),
    thumb: thumb(`client-up-${i}`),
    caption: i === 0
      ? 'Owner site visit — north elevation'
      : i === 1
      ? 'Question about kitchen window placement'
      : i === 2
      ? 'Existing signage to be removed'
      : undefined,
    source: 'client' as const,
    uploaded_at: `2026-04-${String(15 + i * 2).padStart(2, '0')}T10:00:00Z`,
  }));

export const MOCK_PROJECT: MockProject = {
  id: 'mock-wdg013',
  slug: 'wdg013-pittsburgh-pa',
  name: 'WDG013_Pittsburgh_PA',
  client_name: 'Wing District Grill — Pittsburgh',
  city_state: 'Pittsburgh, PA',
  overall_status: 'Active',
  target_completion_date: '2026-12-15',
  color_tag: 'rust',
  last_published_at: '2026-05-09T11:30:00Z',

  // Total photo count surfaced in the header button. Computed from blocks below.
  totalPhotoCount: 24,

  phases: [
    {
      id: 'p-01',
      phase_number: 1,
      phase_name: 'Pre-Development',
      status: 'In Progress',
      percent_complete: 80,
      target_start_date: '2026-04-01',
      target_end_date: '2026-05-15',
      actual_start_date: '2026-04-01',
      narrative:
        'Pre-development is wrapping up cleanly. Site secured under a 10-year lease, full survey and geotechnical reports in hand, and Owner-PM and Owner-Architect agreements executed. Pre-application meeting with City of Pittsburgh scheduled for May 14 to confirm submission requirements before entitlements work begins.',
      tasks: [
        { id: 't-01-1', task_group: 'Site & Feasibility', task_text: 'Site Identified', is_complete: true, completed_date: '2026-04-02' },
        { id: 't-01-2', task_group: 'Site & Feasibility', task_text: 'Site Survey Received', is_complete: true, completed_date: '2026-05-05' },
        { id: 't-01-3', task_group: 'Site & Feasibility', task_text: 'Geotech Received', is_complete: true, completed_date: '2026-05-07' },
        { id: 't-01-4', task_group: 'Agreements', task_text: 'LOI Signed', is_complete: true, completed_date: '2026-04-08' },
        { id: 't-01-5', task_group: 'Agreements', task_text: 'Lease Executed with Landlord', is_complete: true, completed_date: '2026-04-22' },
        { id: 't-01-6', task_group: 'Agreements', task_text: 'Owner-PM Agreement Signed', is_complete: true, completed_date: '2026-04-15' },
        { id: 't-01-7', task_group: 'Agreements', task_text: 'Owner-Architect Agreement Signed', is_complete: true, completed_date: '2026-04-30' },
        { id: 't-01-8', task_group: 'Pre-Application', task_text: 'Pre-Application Meeting with City Held', is_complete: false, due_date: '2026-05-14' },
        { id: 't-01-9', task_group: 'Pre-Application', task_text: 'Initial Budget + Schedule Baseline Established', is_complete: false, is_critical: true, due_date: '2026-05-15' },
      ],
      permits: [],
      updates: [
        {
          id: 'u-01-1',
          date: '2026-05-09T08:30:00Z',
          log_type: 'Site Update',
          body: 'Foundation excavation set to begin the week of May 18. Soils report came back clean — no surprises in the geotechnical investigation. Concrete subcontractor is mobilizing the week after.',
          pinned: true,
        },
        {
          id: 'u-01-2',
          date: '2026-05-05T17:00:00Z',
          log_type: 'Site Update',
          body: 'Site survey complete. Parcel lines verified against city records, no encroachments identified. Civil drawings being updated to reflect actual benchmark elevation.',
          pinned: false,
        },
        {
          id: 'u-01-3',
          date: '2026-04-22T10:00:00Z',
          log_type: 'Agreement',
          body: 'Lease executed with landlord. 10-year primary term with two 5-year options. Tenant improvement allowance secured at $45/sf.',
          pinned: false,
        },
        {
          id: 'u-01-4',
          date: '2026-04-08T09:00:00Z',
          log_type: 'Agreement',
          body: 'LOI signed. Moving to lease negotiation.',
          pinned: false,
        },
      ],
    },
    {
      id: 'p-02',
      phase_number: 2,
      phase_name: 'Entitlements & Approvals',
      status: 'In Progress',
      percent_complete: 25,
      target_start_date: '2026-05-15',
      target_end_date: '2026-08-10',
      tasks: [
        { id: 't-02-1', task_group: 'Approvals', task_text: 'Zoning Confirmed', is_complete: true, completed_date: '2026-05-08' },
        { id: 't-02-2', task_group: 'Approvals', task_text: 'Site Plan Submitted', is_complete: false, due_date: '2026-06-01' },
        { id: 't-02-3', task_group: 'Approvals', task_text: 'Site Plan Approved', is_complete: false, is_critical: true },
        { id: 't-02-4', task_group: 'Approvals', task_text: 'Special Use Permit Approved (if needed)', is_complete: false },
        { id: 't-02-5', task_group: 'Civil', task_text: 'Storm Water Plan Approved', is_complete: false },
        { id: 't-02-6', task_group: 'Civil', task_text: 'Utility Service Agreements Signed', is_complete: false },
      ],
      permits: [
        {
          id: 'pm-02-1',
          permit_type: 'Zoning Verification',
          jurisdiction: 'City of Pittsburgh',
          permit_number: 'ZV-2026-0241',
          status: 'Issued',
          application_date: '2026-05-01',
          issued_date: '2026-05-08',
          published_summary: 'Zoning confirmed for restaurant use. No variance required.',
        },
      ],
      updates: [
        {
          id: 'u-02-1',
          date: '2026-05-08T14:00:00Z',
          log_type: 'Permit',
          body: 'Building permit issued by City of Pittsburgh. Permit #BP-2026-0418. Mechanical and electrical sub-permits expected within the week.',
          pinned: true,
        },
        {
          id: 'u-02-2',
          date: '2026-05-08T11:00:00Z',
          log_type: 'Permit',
          body: 'Zoning verification issued. Restaurant use confirmed without variance.',
          pinned: false,
        },
      ],
    },
    {
      id: 'p-03',
      phase_number: 3,
      phase_name: 'Design',
      status: 'Not Started',
      percent_complete: 0,
      target_start_date: '2026-08-10',
      target_end_date: '2026-11-15',
      tasks: [
        { id: 't-03-1', task_group: 'Design Kickoff', task_text: 'Initial Space Planning — Client Approved', is_complete: false },
        { id: 't-03-2', task_group: 'Design Kickoff', task_text: 'Initial Space Planning — Franchise Approved', is_complete: false, is_critical: true },
        { id: 't-03-3', task_group: 'Final Plans', task_text: 'Final Plans for Review Received', is_complete: false },
        { id: 't-03-4', task_group: 'Final Plans', task_text: 'Final Plans — Franchise Approved', is_complete: false, is_critical: true },
        { id: 't-03-5', task_group: 'Final Plans', task_text: 'Stamped Final Set Issued', is_complete: false },
      ],
      permits: [],
      updates: [],
    },
    {
      id: 'p-04',
      phase_number: 4,
      phase_name: 'Bidding & GC Selection',
      status: 'Not Started',
      percent_complete: 0,
      target_start_date: '2026-11-15',
      target_end_date: '2027-01-15',
      tasks: [
        { id: 't-04-1', task_group: 'Bidding', task_text: 'Bidding Package Submitted to GCs', is_complete: false },
        { id: 't-04-2', task_group: 'Selection', task_text: 'GC Selected', is_complete: false, is_critical: true },
        { id: 't-04-3', task_group: 'Contract', task_text: 'GC Contract Fully Executed', is_complete: false },
      ],
      permits: [],
      updates: [],
    },
    {
      id: 'p-05',
      phase_number: 5,
      phase_name: 'Permitting',
      status: 'Not Started',
      percent_complete: 0,
      target_start_date: '2027-01-15',
      target_end_date: '2027-03-01',
      tasks: [
        { id: 't-05-1', task_text: 'Permit Set Submitted to Expeditor', is_complete: false },
        { id: 't-05-2', task_text: 'All Required Permits Issued', is_complete: false, is_critical: true },
      ],
      permits: [],
      updates: [],
    },
    {
      id: 'p-06',
      phase_number: 6,
      phase_name: 'Site Work & Foundation',
      status: 'Not Started',
      percent_complete: 0,
      target_start_date: '2027-03-01',
      target_end_date: '2027-04-30',
      tasks: [
        { id: 't-06-1', task_group: 'Site Prep', task_text: 'MISS DIG Confirmation Submitted', is_complete: false },
        { id: 't-06-2', task_group: 'Excavation', task_text: 'Excavation Complete', is_complete: false },
        { id: 't-06-3', task_group: 'Foundation', task_text: 'Foundation Inspection Passed', is_complete: false, is_critical: true },
        { id: 't-06-4', task_group: 'Underslab', task_text: 'Slab Poured', is_complete: false },
      ],
      permits: [],
      updates: [],
    },
    {
      id: 'p-07',
      phase_number: 7,
      phase_name: 'Vertical Shell',
      status: 'Not Started',
      percent_complete: 0,
      tasks: [
        { id: 't-07-1', task_text: 'Structural Steel Erected', is_complete: false },
        { id: 't-07-2', task_text: 'Roof Membrane Complete', is_complete: false },
      ],
      permits: [],
      updates: [],
    },
    {
      id: 'p-08',
      phase_number: 8,
      phase_name: 'MEP Rough-In',
      status: 'Not Started',
      percent_complete: 0,
      tasks: [
        { id: 't-08-1', task_text: 'Plumbing Rough-In Complete', is_complete: false },
        { id: 't-08-2', task_text: 'Electrical Rough-In Complete', is_complete: false },
      ],
      permits: [],
      updates: [],
    },
    {
      id: 'p-09',
      phase_number: 9,
      phase_name: 'Close-In',
      status: 'Not Started',
      percent_complete: 0,
      tasks: [
        { id: 't-09-1', task_text: 'Insulation Inspection Passed', is_complete: false },
        { id: 't-09-2', task_text: 'Drywall Finished', is_complete: false },
      ],
      permits: [],
      updates: [],
    },
    {
      id: 'p-10',
      phase_number: 10,
      phase_name: 'Finishes & Equipment',
      status: 'Not Started',
      percent_complete: 0,
      tasks: [
        { id: 't-10-1', task_text: 'FF&E Installed', is_complete: false },
        { id: 't-10-2', task_text: 'Kitchen Equipment Installed', is_complete: false },
      ],
      permits: [],
      updates: [],
    },
    {
      id: 'p-11',
      phase_number: 11,
      phase_name: 'Commissioning & C of O',
      status: 'Not Started',
      percent_complete: 0,
      tasks: [
        { id: 't-11-1', task_text: 'Building Final Inspection Passed', is_complete: false, is_critical: true },
        { id: 't-11-2', task_text: 'C of O Issued', is_complete: false, is_critical: true },
      ],
      permits: [],
      updates: [],
    },
  ],

  // ============================================================
  // Photos — moved out of phases entirely (E.7 portal v2 design).
  // Two top-level groupings: PM curated weekly updates, and
  // separate Client Uploads.
  // ============================================================
  photoBlocks: [
    {
      id: 'pre-construction',
      category: 'pre-construction',
      weeks: [
        {
          id: 'pre-week-1',
          week_label: 'Week of May 5, 2026',
          description: 'Site walk-through — surveying confirmed, soils being sampled.',
          photos: makePMWeeklyPhotos('05-05', 6, [
            'Atwood Street frontage',
            'Existing curb cut',
            'Survey stake — NW corner',
            'Adjacent properties',
            'Forbes Avenue view',
            'Soils sampling rig',
          ]),
        },
        {
          id: 'pre-week-2',
          week_label: 'Week of April 28, 2026',
          description: 'Pre-application site visit — first walk with civil engineer.',
          photos: makePMWeeklyPhotos('04-28', 4, [
            'Site overview',
            'Utility marker — east',
            'Drainage swale — south',
            'Adjacent retail signage',
          ]),
        },
        {
          id: 'pre-week-3',
          week_label: 'Week of April 14, 2026',
          description: 'Initial site inspection at lease execution.',
          photos: makePMWeeklyPhotos('04-14', 3),
        },
      ],
    },
    {
      id: 'construction',
      category: 'construction',
      weeks: [],  // No construction photos yet — phase 06+ hasn't started
    },
  ],

  // Client uploads — kept SEPARATE from PM weekly timeline.
  clientUploads: makeClientUploads(8),
};
